import {assertAndContinue} from 'Asserts';
import {
  CoreTableType,
  PerSchemaTableType,
  RelationshipType,
  SqlColumnType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ImmutableMap from 'util/ImmutableMap';

import {ListWarehouseModelsQuery} from './__generated/ListWarehouseModelsQuery';
import sortedTableColumns from './sortedTableColumns';

type WarehouseTableNode =
  ListWarehouseModelsQuery['warehouseModelListing']['warehouseModels'][number]['tables'][number];
type WarehouseModelNode = ListWarehouseModelsQuery['warehouseModelListing']['warehouseModels'][number];
type WarehouseUserDefinedViewMetadataPermissions = Omit<
  NonNullable<WarehouseTableNode['warehouseUserDefinedViewMetadata']>['permissions'],
  '__typename'
>;

export interface WarehouseUserDefinedViewMetadata {
  id: string;
  viewQuery: string;
  existsInWarehouse: boolean;
  permissions: WarehouseUserDefinedViewMetadataPermissions;
}
export interface WarehouseTableData {
  schemaName: string; // Postgres schema the table lives in, e.g. "benchling" in benchling.strain$raw
  name: string;
  columns: Array<WarehouseColumnData>;
  coreTableType: CoreTableType | null;
  schema: WarehouseTableNode['schema'];
  perSchemaTableType: PerSchemaTableType | null;
  description: string | null;
  warehouseUserDefinedViewMetadata: WarehouseUserDefinedViewMetadata | null;
}

export interface WarehouseUserDefinedViewMetadataFull {
  id: string;
  viewQuery: string;
  existsInWarehouse: boolean;
  name: string;
  description: string;
  permissions: WarehouseUserDefinedViewMetadataPermissions;
}

export type MinimalWarehouseTableData = Pick<WarehouseTableData, 'schemaName' | 'name'>;

export interface WarehouseColumnData {
  name: string;
  type: SqlColumnType;
  isMulti: boolean;
  table: WarehouseTableData;
  relationships: Array<WarehouseRelationshipData>;
  index: number; // 0-indexed integer position of this column in the table's column list
}

export interface WarehouseRelationshipData {
  source: WarehouseColumnData;
  target: WarehouseColumnData;
  type: RelationshipType;
  id: number; // Unique identifier for this relationship within the WarehouseModelData.relationships list
}

export interface WarehouseModelData {
  tables: Map<string, WarehouseTableData>; // tablename key -> table data value
  relationships: Array<WarehouseRelationshipData>;
}

export type UserWarehouseModelsData = Map<string, WarehouseModelData>; // schema name key

interface TableColumnPair {
  tableName: string;
  columnName: string;
}

export interface WarehouseModelOptions {
  sortColumns: boolean;
}

export function getTableDataFromModelsData(
  schemaName: string,
  tableName: string,
  warehouseModelsData: UserWarehouseModelsData
): WarehouseTableData | null {
  const tableData = warehouseModelsData.get(schemaName)?.tables.get(tableName);

  if (tableData === undefined) {
    assertAndContinue(false, 'Schema or table does not exist in loaded warehouse models');
    return null;
  }

  return tableData;
}

/**
 * Given a list of values objs and a value -> key function keyFn, return a keyFn(value) -> value Map.
 */
function indexArrayIntoMap<TKey, TObj>(objs: Array<TObj>, keyFn: (obj: TObj) => TKey): Map<TKey, TObj> {
  const result = new Map();
  for (const obj of objs) {
    result.set(keyFn(obj), obj);
  }
  return result;
}

/**
 * Given a GraphQL table node, return an WarehouseTableData object with the list of columns filled in with
 * each column backreferencing the table. Relationships are not yet filled in.
 */
export function tableNodeToTableData(
  tableNode: WarehouseTableNode,
  sortColumns: boolean
): WarehouseTableData {
  const tableData: WarehouseTableData = {
    schemaName: tableNode.schemaName,
    name: tableNode.tableName,
    columns: [],
    coreTableType: tableNode.coreTableType,
    schema: tableNode.schema,
    perSchemaTableType: tableNode.perSchemaTableType,
    description: tableNode.description,
    warehouseUserDefinedViewMetadata: tableNode.warehouseUserDefinedViewMetadata,
  };

  tableData.columns = (sortColumns ? sortedTableColumns(tableNode.columns) : tableNode.columns).map(
    ({name, type, isMulti}, index) => ({
      name,
      type,
      isMulti,
      table: tableData,
      relationships: [],
      index,
    })
  );

  return tableData;
}

export function transformWarehouseModel(
  model: WarehouseModelNode,
  warehouseModelOptions?: WarehouseModelOptions
): WarehouseModelData {
  // Populate map of tablename -> WarehouseTableData object
  const tables = indexArrayIntoMap<string, WarehouseTableData>(
    model.tables.map((tableNode) =>
      tableNodeToTableData(tableNode, warehouseModelOptions?.sortColumns ?? false)
    ),
    (tableData) => tableData.name
  );

  const nameToDataMap = new ImmutableMap<TableColumnPair, WarehouseColumnData>(
    Array.from(tables.entries()).flatMap(([tableName, tableData]) =>
      tableData.columns.map((columnData: WarehouseColumnData): [TableColumnPair, WarehouseColumnData] => [
        {
          tableName,
          columnName: columnData.name,
        },
        columnData,
      ])
    )
  );
  let id = 0;
  const relationships = model.tables.flatMap((tableNode) =>
    tableNode.columns.flatMap((columnNode) =>
      columnNode.columnRelationships.flatMap((relationshipNode) => {
        const source = nameToDataMap.get({tableName: tableNode.tableName, columnName: columnNode.name});
        const target = nameToDataMap.get({
          tableName: relationshipNode.targetColumn.table.tableName,
          columnName: relationshipNode.targetColumn.name,
        });
        if (!(source && target)) {
          assertAndContinue(false, 'Invalid relationship in warehouse diagram model');
          return [];
        }
        return {
          source,
          target,
          type: relationshipNode.type,
          id: id++,
        };
      })
    )
  );

  for (const relationship of relationships) {
    relationship.source.relationships.push(relationship);
    relationship.target.relationships.push(relationship);
  }

  return {tables, relationships};
}

/**
 * Transform GraphQL representations of warehouse models into a more usable frontend representation.
 *
 * At a high level, returns a map between all the schemas the current user is in and a collection of data
 * about the tables in that schema.
 *
 * The information includes table names, the columns/column types in
 * that table, and for each column any conceptual "foreign key" relationships that column has to other
 * columns in the schema.
 *
 * On a higher data-level, the information is a list of table objects, each of which has a list of column objects,
 * each of which has a backreference to the table it's in as well as a list of relationship objects. Each
 * relationship object has its relationship type and its source and target columns.
 *
 * Return type schema:
 *   Map(schemaName -> {
 *    tables: Map(tableName -> {
 *      tableName: string,
 *      schemaName: string,
 *      coreTableType: CoreTableType | null,
 *      columns: List[{columnName, columnType, tablePntr, relationships, index}]
 *    })
 *    relationships: [{sourceColumnPntr, targetColumnPntr, relationshipType, id}]
 *  })
 */
export function warehouseModelGQLToRelationalData(
  data: ListWarehouseModelsQuery,
  warehouseModelOptions?: WarehouseModelOptions
): UserWarehouseModelsData {
  const output: UserWarehouseModelsData = new Map(); // schema name -> DiagramData

  for (const model of data.warehouseModelListing.warehouseModels) {
    output.set(model.schemaName, transformWarehouseModel(model, warehouseModelOptions));
  }

  return output;
}
